import { Badge, EmptyState, Icon, Pagination, Spinner } from '@blend-ed/blendx-ui';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import useFetchNotesOfCourse from '../hooks/notes/useFetchNotesOfCourse';
import messages from './messages';

const highlightText = (text) => {
  if (!text) return '';

  return text
    .replace(/{elasticsearch_highlight_start}/g, '<span class="highlight">')
    .replace(/{elasticsearch_highlight_end}/g, '</span>');
};

const Note = ({ note, intl }) => {
  return (
    <div className="note-card">
      <div className="note-card__content">
        <div className="note-card__quote">
          <div className="note-card__main">
            <a href={note.link} className="quote-link" target="_blank" rel="noopener noreferrer">
              "<span dangerouslySetInnerHTML={{ __html: highlightText(note.quote) }} />"
            </a>
            {note.tags.map((tag, index) => (
              <Badge key={index} variant="light-success" className="note-card__tag">
                <span dangerouslySetInnerHTML={{ __html: highlightText(tag) }} />
              </Badge>
            ))}
          </div>
          <div className="note-card__date">
            {format(new Date(note.created), 'MMM dd, yyyy')}
          </div>
        </div>
        <div className="note-card__text">
          <span dangerouslySetInnerHTML={{ __html: highlightText(note.text) }} />
        </div>
      </div>
      <a href={note.link} className="note-card__link text-sm" target="_blank" rel="noopener noreferrer">
        <Icon icon="external-link" type="line" size="xs" />
        <span className="text-subtle">{intl.formatMessage(messages.viewSourceLink)}</span>
      </a>
    </div>
  );
};

Note.propTypes = {
  intl: intlShape.isRequired,
};

const Notes = ({ courseId, searchValue, intl }) => {
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const [isPaginationFixed, setIsPaginationFixed] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE);

  const { data, isLoading } = useFetchNotesOfCourse(
    courseId,
    {
      page,
      pageSize: itemsPerPage,
      text: searchValue
    }
  );

  useEffect(() => {
    setPage(1); // Reset to first page when search value changes
  }, [searchValue]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const threshold = 64;
      setIsPaginationFixed(scrollTop > threshold);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  if (!courseId) {
    return <div className="text-center">{intl.formatMessage(messages.selectCourseMessage)}</div>;
  }

  if (isLoading) {
    return (
      <div className="blendx-spinner-container">
        <Spinner animation="border" size="lg" variant="dark" />
      </div>
    )
  }

  const totalPages = data?.notes_of_students.num_pages;
  const currentPage = data?.notes_of_students.current_page;
  const nextPage = data?.notes_of_students.next;
  const previousPage = data?.notes_of_students.previous;

  if (!data?.notes_of_students.results.length) {
    return <EmptyState
      type="box"
      message={intl.formatMessage(messages.noNotesFound)}
      className="notes-empty-state"
    />
  }

  return (
    <div className="notes-container">
      {data?.notes_of_students.results.map((note) => (
        <Note key={note.id} note={note} intl={intl} />
      ))}
      {totalPages > 1 && (
        <div className={classNames('notes-pagination-footer', { 'fixed-footer': isPaginationFixed })}>
          <Pagination
            className="pagination"
            previousPage={previousPage}
            nextPage={nextPage}
            gotoPage={(page) => setPage(page)}
            totalPages={totalPages}
            currentPage={currentPage}
            canPreviousPage={previousPage}
            canNextPage={nextPage}
            itemsPerPage={itemsPerPage}
            changeItemsPerPage={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage);
              setPage(1);
            }}
          />
        </div>
      )}
    </div>
  );
};

Notes.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(Notes);
